import React, { Component } from 'react';
import { connect } from 'react-redux';

import { updateAddReplyId } from 'actions/comments';
import {
  requestVideoPlayerCurrentTime,
  requestVideoPlayerPlay,
  requestVideoPlayerVolume,
  startVideoWithoutPlaying,
  updateVideoPlayerAutoplay,
  updateVideoPlayerFlippedCamera,
} from 'actions/videoPlayer';
import {
  EMBED_PLAY_VIDEO_AT_TS,
  EMBED_UPDATE_TIME,
  SHARE_VIDEO_FLIP_CAMERA,
  TRIM_ACTION_DISPATCHED,
} from 'constants/messages';
import {
  ADD_TRIM_OPERATION,
  ENTER_TRIM_MODE_OPERATION,
  LEAVE_TRIM_MODE_OPERATION,
  REDO_TRIM_OPERATION,
  REMOVE_TRIM_OPERATION,
  UNDO_TRIM_OPERATION,
  UPDATE_CURRENT_TRIM_RANGE_OPERATION,
  UPDATE_SELECTED_TRIM_BOUND_OPERATION,
  UPDATE_SELECTED_TRIM_RANGE_OPERATION,
  UPDATE_TRIM_OPERATION,
} from 'constants/trimming';
import { REPLY_TO_COMMENT_PARAM } from 'constants/urlParams';
import { getFeatureFlagCreator } from 'creators/featureFlag';
import {
  addTrimRangeCreator,
  enterTrimModeCreator,
  leaveTrimModeCreator,
  redoTrimOperationCreator,
  removeTrimRangeCreator,
  undoTrimOperationCreator,
  updateCurrentTrimRangeCreator,
  updateSelectedTrimBoundCreator,
  updateSelectedTrimRangeCreator,
  updateTrimRangeCreator,
} from 'creators/trimming';
import { fetchLastWatchedTime } from 'creators/videoPlayer';
import { selectEmbed } from 'selectors/embed';
import { isMobile } from 'utilities/device';
import * as sentry from 'utilities/sentry';
import { getParam } from 'utilities/url';
import { getPageLoadParam } from 'utilities/urlParams';
import VideoSession from 'utilities/video-session';

import VideoPlayer from 'components/video-player';

import './styles.less';

export const EmbedContext = React.createContext({});

// 30% of total volume - used after a recording has completed
const MUTED_AUDIO_VOLUME = 0.3;

const INVALID_TRIM_OPERATION_ERROR = new Error('invalid trim operation');

class EmbedVideo extends Component {
  render() {
    const {
      embedContext = {},
      EditVideoButtons,
      ViewerVideoButtons,
      stylesFromShareVideoParent = {},
    } = this.props;

    return (
      <EmbedContext.Provider value={embedContext}>
        <VideoPlayer
          EditVideoButtons={EditVideoButtons}
          ViewerVideoButtons={ViewerVideoButtons}
          stylesFromShareVideoParent={stylesFromShareVideoParent}
        />
      </EmbedContext.Provider>
    );
  }

  constructor(props) {
    super(props);

    if (props.videoAnalyticsEnabled && !props.isOwner) {
      const { videoId, trimId, userId } = props;

      VideoSession.start({ videoId, userId, trimId });
    }
  }

  componentDidMount() {
    this.updatePlayerFromURLParams();
    this.listenForSharePageEvents();

    if (this.props.isMainVideoOnSharePage) {
      return;
    }

    document.body.style.overflow = 'hidden';
    document.getElementById('container').style.overflow = 'hidden';
  }

  // grab url params on the embed iframe that dictate different behaviors of
  // the video player
  updatePlayerFromURLParams() {
    // we use autoplay and auto_play because Twitter uses a combo of these
    // params, which means they probably have historical reasons for doing so
    const autoplay =
      !isMobile &&
      (getParam('autoplay') === '1' ||
        getParam('autoplay') === 'true' ||
        getParam('auto_play') === 'true' ||
        getParam('auto_play') === '1');

    this.props.updateVideoPlayerAutoplay(autoplay);

    const mutedAudio =
      getParam('muted') === '1' || getParam('muted') === 'true';

    if (mutedAudio) {
      this.props.requestVideoPlayerVolume(MUTED_AUDIO_VOLUME);
    }

    // Two formats: 'XmYs' or just 'Y' where Y is number of seconds,
    // e.g. 1m20s or 80s or 80 => all values mean 80 seconds
    const time = getPageLoadParam('t');
    const startAndPause = getPageLoadParam('start_and_pause');
    const replyToComment = getPageLoadParam(REPLY_TO_COMMENT_PARAM);
    const shouldStartAndPause = startAndPause === '1';

    const defaultTime = [0, 0, 0, 0, 0];
    const [, , min, , sec] = time.match(/((\d+)m)?((\d+)s?)$/) || defaultTime;
    const seekTime = Number(min || 0) * 60 + Number(sec || 0);

    if (seekTime > 0) {
      this.props.requestVideoPlayerCurrentTime(seekTime);
    } else {
      this.props.fetchLastWatchedTime(this.props.videoId);
    }

    if (shouldStartAndPause) {
      this.props.startVideoWithoutPlaying();
    }

    if (replyToComment) {
      this.props.updateAddReplyId(replyToComment);
    }
  }

  listenForSharePageEvents() {
    const { isMainVideoOnSharePage } = this.props;

    if (!isMainVideoOnSharePage) {
      return;
    }

    window.top.addEventListener(
      'message',
      e => {
        const { data: eventData = {} } = e;

        switch (eventData.type) {
          case EMBED_PLAY_VIDEO_AT_TS:
            this.playVideoAtTimestamp(eventData.numSeconds);
            break;
          case EMBED_UPDATE_TIME:
            this.props.requestVideoPlayerCurrentTime(eventData.numSeconds);
            break;
          case SHARE_VIDEO_FLIP_CAMERA:
            this.props.updateVideoPlayerFlippedCamera(eventData.flipped_camera);
            break;
          case TRIM_ACTION_DISPATCHED:
            this.handleTrimAction(eventData);
            break;
          default:
            break;
        }
      },
      false
    );
  }

  playVideoAtTimestamp(numSeconds) {
    this.props.requestVideoPlayerCurrentTime(numSeconds);
    this.props.requestVideoPlayerPlay();
  }

  handleTrimAction(eventData) {
    const { id, operation, trimBound, trimRange, trimRanges, updatedVideo } =
      eventData;

    switch (operation) {
      case ADD_TRIM_OPERATION:
        return this.props.addTrimRangeCreator(trimRange);
      case REMOVE_TRIM_OPERATION:
        return this.props.removeTrimRangeCreator(id);
      case ENTER_TRIM_MODE_OPERATION:
        return this.props.enterTrimModeCreator(trimRanges);
      case LEAVE_TRIM_MODE_OPERATION:
        return this.props.leaveTrimModeCreator(updatedVideo);
      case REDO_TRIM_OPERATION:
        return this.props.redoTrimOperationCreator();
      case UNDO_TRIM_OPERATION:
        return this.props.undoTrimOperationCreator();
      case UPDATE_CURRENT_TRIM_RANGE_OPERATION:
        return this.props.updateCurrentTrimRangeCreator(trimRange);
      case UPDATE_SELECTED_TRIM_BOUND_OPERATION:
        return this.props.updateSelectedTrimBoundCreator(trimBound);
      case UPDATE_SELECTED_TRIM_RANGE_OPERATION:
        return this.props.updateSelectedTrimRangeCreator(trimRange);
      case UPDATE_TRIM_OPERATION:
        return this.props.updateTrimRangeCreator(trimRange);
      default:
        sentry.logException(
          INVALID_TRIM_OPERATION_ERROR,
          'bad trim operation',
          {
            operation,
          }
        );
    }
  }
}
const mapStateToProps = state => selectEmbed(state);

const mapDispatchToProps = dispatch => {
  return {
    addTrimRangeCreator: trimRange =>
      dispatch(addTrimRangeCreator(trimRange, false, false)),
    enterTrimModeCreator: trimRanges =>
      dispatch(enterTrimModeCreator(trimRanges, false)),
    getFeatureFlag: featureFlag => dispatch(getFeatureFlagCreator(featureFlag)),
    leaveTrimModeCreator: updatedVideo =>
      dispatch(leaveTrimModeCreator(updatedVideo, false)),
    redoTrimOperationCreator: () => dispatch(redoTrimOperationCreator(false)),
    removeTrimRangeCreator: trimRangeId =>
      dispatch(removeTrimRangeCreator(trimRangeId, false, false)),
    requestVideoPlayerCurrentTime: currentTime =>
      dispatch(requestVideoPlayerCurrentTime(currentTime)),
    startVideoWithoutPlaying: () => dispatch(startVideoWithoutPlaying()),
    requestVideoPlayerPlay: () => dispatch(requestVideoPlayerPlay()),
    requestVideoPlayerVolume: volume =>
      dispatch(requestVideoPlayerVolume(volume)),
    undoTrimOperationCreator: () => dispatch(undoTrimOperationCreator(false)),
    updateCurrentTrimRangeCreator: trimRange =>
      dispatch(updateCurrentTrimRangeCreator(trimRange, false)),
    updateSelectedTrimBoundCreator: trimBound =>
      dispatch(updateSelectedTrimBoundCreator(trimBound, false)),
    updateSelectedTrimRangeCreator: trimRange =>
      dispatch(updateSelectedTrimRangeCreator(trimRange, false)),
    updateTrimRangeCreator: trimRange =>
      dispatch(updateTrimRangeCreator(trimRange, false, false)),
    updateVideoPlayerAutoplay: autoplay =>
      dispatch(updateVideoPlayerAutoplay(autoplay)),
    updateVideoPlayerFlippedCamera: flippedCamera =>
      dispatch(updateVideoPlayerFlippedCamera(flippedCamera)),
    updateAddReplyId: replyId => dispatch(updateAddReplyId(replyId)),
    fetchLastWatchedTime: videoId => dispatch(fetchLastWatchedTime(videoId)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(EmbedVideo);
